import React from 'react';

import { RatchetModel, RatchetModelsEnum } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';

import { conversionModelsThresholdsMap } from 'src/components/AddPrimeAMFRateCard/AddContractTerms/AddConversionModel/conversionModelThresholds';
import { Toggle } from 'src/components/Toggle';
import { ViewConversionModelDefinitionsTable } from 'src/components/ViewConversionModelDefinitionsTable';
import { ContractTermSection } from 'src/components/shared/ContractTermSection';
import { AppContext } from 'src/contexts/appContext';
import { getThresholdDisplayName } from 'src/util/thresholdUtil';

import './ViewConversionModel.scss';

interface IViewConversionModelProps {
    conversion?: RatchetModel;
    guardrailConversion?: RatchetModel;
    serviceId: number;
}

export const ViewConversionModel = ({ conversion, guardrailConversion, serviceId }: IViewConversionModelProps) => {
    const { metadata } = React.useContext(AppContext);
    const services = metadata.serviceMapping;
    const hasConversion = !!conversion;
    const serviceName = serviceId === services[2].serviceId ? 'Prime' : 'AMF';
    const disabledText = 'Enable variable rates based on conversion performance';
    const ratchets =
        conversion?.ratchetModel === RatchetModelsEnum.PRIME_CONVERSION_UMG ||
        conversion?.ratchetModel === RatchetModelsEnum.PRIME_CONVERSION_FLOOR_UMG
            ? conversionModelsThresholdsMap[conversion?.ratchetModel]
            : conversion?.ratchets;

    // Combine ratchetDefinitions if guardrailConversion exists
    const combinedRatchetDefinitions = guardrailConversion
        ? [...(conversion?.ratchetDefinitions || []), ...(guardrailConversion?.ratchetDefinitions || [])]
        : conversion?.ratchetDefinitions;

    // Modify description text if guardrailConversion exists
    let descriptionText = '';
    if (hasConversion) {
        if (guardrailConversion && conversion?.description) {
            descriptionText = conversion.description.replace('Primary', '').trim();
        } else {
            descriptionText = conversion?.description || '';
        }
    }

    return (
        <ContractTermSection title={`${serviceName} conversions`}>
            <Toggle
                label={hasConversion ? 'Conversion model: ' : disabledText}
                text={descriptionText}
                value={hasConversion}
                isEditMode={false}
                inlineLabel={hasConversion}
            />
            {hasConversion && (
                <Row alignmentVertical="top">
                    <Column spacing="300" width={250}>
                        <Text type="h300">Rate tiers</Text>
                        <Table headerRows={1} showStripes={false} showDividers={true}>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>Threshold</TableCell>
                            </TableRow>
                            {ratchets?.map((ratchet, index) => (
                                <TableRow key={index}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{getThresholdDisplayName(ratchet)}</TableCell>
                                </TableRow>
                            ))}
                        </Table>
                    </Column>
                    <ViewConversionModelDefinitionsTable ratchetDefinitions={combinedRatchetDefinitions} />
                </Row>
            )}
        </ContractTermSection>
    );
};
